// src/components/sections/DoctorsSection.js
import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaGraduationCap, FaAward, FaStethoscope, FaStar, FaHeartbeat } from 'react-icons/fa';

const DoctorsSection = () => {
    const doctors = [
        {
            name: "Dr. Sarah Johnson",
            specialty: "Gynecology & Obstetrics",
            experience: "25+ years",
            education: "MD, Harvard Medical School",
            image: "https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop&crop=face",
            color: "#20B2AA",
            achievements: ["Board Certified", "Published 50+ Papers", "Teaching Faculty"]
        },
        {
            name: "Dr. Michael Chen",
            specialty: "Pediatric Surgery",
            experience: "20+ years",
            education: "MS, Johns Hopkins University",
            image: "https://images.pexels.com/photos/5327585/pexels-photo-5327585.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop&crop=face",
            color: "#87CEEB",
            achievements: ["Fellowship Trained", "Minimal Access Surgery Expert", "Award Winner"]
        },
        {
            name: "Dr. Emily Rodriguez",
            specialty: "Fertility Specialist",
            experience: "18+ years",
            education: "PhD Reproductive Medicine",
            image: "https://images.pexels.com/photos/733872/pexels-photo-733872.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop&crop=face",
            color: "#FF7F50",
            achievements: ["IVF Specialist", "Research Pioneer", "Patient Choice Award"]
        },
        {
            name: "Dr. James Wilson",
            specialty: "Neonatal Care",
            experience: "22+ years",
            education: "MD, Stanford University",
            image: "https://images.pexels.com/photos/3756681/pexels-photo-3756681.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop&crop=face",
            color: "#9370DB",
            achievements: ["NICU Director", "Fellowship Program Head", "Safety Award"]
        },
    ];

    return (
        <section id="doctors" className="section-padding" style={{
            backgroundColor: '#0c2d4a',
            background: 'linear-gradient(135deg, #0a1a2a 0%, #1a3a5f 100%)',
            position: 'relative',
            overflow: 'hidden'
        }}>

            {/* Subtle background pattern */}
            <div className="position-absolute w-100 h-100" style={{ zIndex: 0 }}>
                {/* Grid pattern */}
                <div className="position-absolute w-100 h-100" style={{
                    backgroundImage: `linear-gradient(rgba(32, 178, 170, 0.03) 1px, transparent 1px),
                                     linear-gradient(90deg, rgba(32, 178, 170, 0.03) 1px, transparent 1px)`,
                    backgroundSize: '50px 50px',
                    animation: 'patternMove 60s linear infinite'
                }} />

                {/* Floating medical icons */}
                {Array.from({ length: 8 }).map((_, i) => {
                    const icons = ['❤️', '🩺', '💊', '🏥', '👶', '🌡️', '🩹', '⭐'];
                    const size = 20 + Math.random() * 30;
                    const left = `${Math.random() * 100}%`;
                    const top = `${Math.random() * 100}%`;
                    const duration = 20 + Math.random() * 20;

                    return (
                        <motion.div
                            key={i}
                            className="position-absolute"
                            style={{
                                left,
                                top,
                                fontSize: `${size}px`,
                                opacity: 0.05,
                                filter: 'blur(0.5px)'
                            }}
                            animate={{
                                y: [0, -30, 0],
                                x: [0, Math.random() * 20 - 10, 0],
                                rotate: [0, 180, 360]
                            }}
                            transition={{
                                duration,
                                repeat: Infinity,
                                ease: "linear"
                            }}
                        >
                            {icons[i % icons.length]}
                        </motion.div>
                    );
                })}
            </div>

            <Container style={{ position: 'relative', zIndex: 1 }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-5"
                >
                    <h2 className="display-5 fw-bold mb-3 text-white">
                        Our Medical Leadership
                    </h2>
                    <p className="lead" style={{
                        color: 'rgba(255, 255, 255, 0.85)',
                        maxWidth: '700px',
                        margin: '0 auto',
                        fontSize: '1.2rem'
                    }}>
                        Led by renowned specialists with decades of experience in women's and children's healthcare.
                    </p>
                </motion.div>

                <Row className="g-4">
                    {doctors.map((doctor, index) => (
                        <Col lg={3} md={6} key={index}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -10 }}
                                style={{ height: '100%' }}
                            >
                                <Card className="healthcare-card h-100 border-0 position-relative d-flex flex-column"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                                        overflow: 'hidden',
                                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                        minHeight: '650px'
                                    }}
                                >
                                    {/* Doctor Photo */}
                                    <div className="position-relative overflow-hidden" style={{ height: '220px', flexShrink: 0 }}>
                                        <Card.Img
                                            variant="top"
                                            src={doctor.image}
                                            alt={doctor.name}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                transition: 'transform 0.5s ease'
                                            }}
                                            onError={(e) => {
                                                // Fallback image if Pexels fails
                                                e.target.src = `https://ui-avatars.com/api/?name=${doctor.name.replace(' ', '+')}&background=${doctor.color.replace('#', '')}&color=fff&size=400&bold=true`;
                                                e.target.style.objectFit = 'cover';
                                            }}
                                        />

                                        {/* Overlay gradient */}
                                        <div className="position-absolute bottom-0 left-0 w-100"
                                            style={{
                                                height: '100px',
                                                background: 'linear-gradient(to top, rgba(0,0,0,0.4), transparent)'
                                            }}
                                        />

                                        {/* Specialty badge */}
                                        <div className="position-absolute bottom-3 left-3">
                                            <span className="badge px-3 py-2 fw-bold"
                                                style={{
                                                    backgroundColor: doctor.color,
                                                    fontSize: '0.8rem'
                                                }}
                                            >
                                                {doctor.specialty}
                                            </span>
                                        </div>

                                        {/* Experience badge */}
                                        <div className="position-absolute top-3 right-3">
                                            <span className="badge px-3 py-2 fw-bold"
                                                style={{
                                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                                    color: 'white',
                                                    fontSize: '0.8rem'
                                                }}
                                            >
                                                {doctor.experience}
                                            </span>
                                        </div>
                                    </div>

                                    <Card.Body className="p-4 position-relative d-flex flex-column flex-grow-1" style={{ zIndex: 2 }}>
                                        <div className="flex-grow-1">
                                            <Card.Title className="fw-bold mb-2" style={{
                                                color: 'var(--dark-teal)',
                                                fontSize: '1.3rem'
                                            }}>
                                                {doctor.name}
                                            </Card.Title>

                                            <div className="mb-3" style={{ fontSize: '0.9rem' }}>
                                                <div className="d-flex align-items-center mb-2">
                                                    <FaGraduationCap className="me-2" style={{ color: doctor.color, width: '16px' }} />
                                                    <span style={{ color: '#555' }}>{doctor.education}</span>
                                                </div>
                                                <div className="d-flex align-items-center mb-3">
                                                    <FaAward className="me-2" style={{ color: doctor.color, width: '16px' }} />
                                                    <span style={{ color: '#555' }}>{doctor.experience} experience</span>
                                                </div>
                                            </div>

                                            {/* Achievements */}
                                            <div className="mb-4">
                                                <h6 className="fw-bold mb-2" style={{
                                                    color: doctor.color,
                                                    fontSize: '0.9rem'
                                                }}>
                                                    Key Achievements:
                                                </h6>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {doctor.achievements.map((achievement, idx) => (
                                                        <span
                                                            key={idx}
                                                            className="badge px-2 py-1"
                                                            style={{
                                                                backgroundColor: `${doctor.color}20`,
                                                                color: doctor.color,
                                                                fontWeight: '500',
                                                                fontSize: '0.75rem'
                                                            }}
                                                        >
                                                            {achievement}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* View Profile Button - Fixed at bottom */}
                                        <div className="mt-auto pt-3">
                                            <motion.button
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                className="btn w-100"
                                                style={{
                                                    backgroundColor: doctor.color,
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    padding: '10px',
                                                    fontWeight: '600',
                                                    fontSize: '0.9rem',
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                <FaHeartbeat className="me-2" />
                                                View Full Profile
                                            </motion.button>
                                        </div>
                                    </Card.Body>

                                    {/* Simplified Hover Overlay - Single Color */}
                                    <div className="position-absolute top-0 left-0 w-100 h-100 doctor-card-overlay"
                                        style={{
                                            backgroundColor: '#0a1a2a',
                                            opacity: 0,
                                            transition: 'all 0.4s ease',
                                            zIndex: 1,
                                            padding: '2rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            textAlign: 'center',
                                            border: `2px solid ${doctor.color}`
                                        }}
                                    >
                                        <div className="text-white">
                                            <FaStar className="mb-3" size={32} style={{ color: '#FFD700' }} />
                                            <h5 className="fw-bold mb-3" style={{ color: doctor.color }}>
                                                {doctor.name.split(' ')[0]}
                                            </h5>
                                            <p className="mb-3 opacity-90" style={{ fontSize: '0.9rem' }}>
                                                {doctor.specialty}
                                            </p>

                                            <div className="d-flex justify-content-center mb-3">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <FaStar key={star} className="mx-1" style={{ color: '#FFD700' }} />
                                                ))}
                                            </div>

                                            <p className="mb-4 opacity-75" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                                                "Committed to providing exceptional care with compassion and expertise."
                                            </p>

                                            <motion.button
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                className="btn"
                                                style={{
                                                    backgroundColor: doctor.color,
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    padding: '8px 20px',
                                                    fontWeight: '600',
                                                    fontSize: '0.85rem'
                                                }}
                                            >
                                                <FaStethoscope className="me-2" />
                                                Book Consultation
                                            </motion.button>
                                        </div>
                                    </div>
                                </Card>
                            </motion.div>
                        </Col>
                    ))}
                </Row>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mt-5 pt-4"
                >
                    <div className="p-4 rounded d-inline-block glass-effect"
                        style={{
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}
                    >
                        <h4 className="fw-bold mb-3 text-white">
                            Meet Our Complete Team
                        </h4>
                        <p className="mb-4 text-white opacity-90" style={{ maxWidth: '600px', margin: '0 auto' }}>
                            Our team includes 50+ specialists across various departments,
                            all dedicated to providing comprehensive healthcare.
                        </p>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="btn-sky-blue px-5 py-3"
                            style={{ borderRadius: '50px', fontWeight: '600' }}
                        >
                            <FaStethoscope className="me-2" />
                            Meet All Our Doctors
                        </motion.button>
                    </div>
                </motion.div>
            </Container>

            <style jsx="true">{`
                @keyframes patternMove {
                    0% { background-position: 0 0; }
                    100% { background-position: 50px 50px; }
                }

                /* Ensure all cards have equal height */
                .healthcare-card {
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                }

                .doctor-card-overlay {
                    opacity: 0;
                    visibility: hidden;
                }

                .healthcare-card:hover .doctor-card-overlay {
                    opacity: 1;
                    visibility: visible;
                }

                .healthcare-card:hover img {
                    transform: scale(1.05);
                }

                /* Responsive adjustments */
                @media (max-width: 1200px) {
                    .healthcare-card {
                        min-height: 600px !important;
                    }
                }

                @media (max-width: 992px) {
                    .healthcare-card {
                        min-height: 580px !important;
                    }
                }

                @media (max-width: 768px) {
                    .healthcare-card {
                        min-height: 550px !important;
                    }
                }

                @media (max-width: 576px) {
                    .healthcare-card {
                        min-height: 520px !important;
                    }
                }
            `}</style>
        </section>
    );
};

export default DoctorsSection;